import type { FilterConfig } from '@/types/filters'

/**
 * LOTE 8: Configurações de filtros para campos finais e customizações
 * 
 * METADADOS E AUDITORIA (8 campos):
 * - Data de Criação
 * - Data de Última Atualização
 * - Criado Por
 * - Última Atualização Por
 * - Número de Atualizações
 * - Status de Sincronização
 * - Fonte de Dados
 * - Última Sincronização
 * 
 * CAMPOS CUSTOMIZÁVEIS (7 campos):
 * - Campo Customizado 1
 * - Campo Customizado 2
 * - Campo Customizado 3
 * - Tags/Labels
 * - Observações Internas
 * - Prioridade
 * - Status Customizado
 */

export const LOTE_8_FILTER_CONFIGS: FilterConfig[] = [
  // ===== METADADOS E AUDITORIA =====
  {
    columnId: 'createdAt',
    label: 'Data de Criação',
    type: 'date',
    operators: ['equals', 'greaterThan', 'lessThan', 'between'],
    placeholder: 'dd/mm/aaaa',
    category: 'Auditoria'
  },
  {
    columnId: 'updatedAt',
    label: 'Data de Última Atualização',
    type: 'date',
    operators: ['equals', 'greaterThan', 'lessThan', 'between'],
    placeholder: 'dd/mm/aaaa',
    category: 'Auditoria'
  },
  {
    columnId: 'createdBy',
    label: 'Criado Por',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains'],
    options: [], // Será populado dinamicamente com usuários
    placeholder: 'Selecione quem criou...',
    category: 'Auditoria'
  },
  {
    columnId: 'lastUpdatedBy',
    label: 'Última Atualização Por',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains'],
    options: [], // Será populado dinamicamente com usuários
    placeholder: 'Selecione quem atualizou...',
    category: 'Auditoria'
  },
  {
    columnId: 'updateCount',
    label: 'Número de Atualizações',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 5',
    category: 'Auditoria'
  },
  {
    columnId: 'syncStatus',
    label: 'Status de Sincronização',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'synchronized', label: 'Sincronizado' },
      { value: 'pending', label: 'Pendente' },
      { value: 'error', label: 'Erro na Sincronização' },
      { value: 'manual', label: 'Atualização Manual' },
      { value: 'outdated', label: 'Desatualizado' }
    ],
    placeholder: 'Selecione o status...',
    category: 'Auditoria'
  },
  {
    columnId: 'dataSource',
    label: 'Fonte de Dados',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'manual', label: 'Entrada Manual' },
      { value: 'api_import', label: 'Importação API' },
      { value: 'csv_import', label: 'Importação CSV' },
      { value: 'system_generated', label: 'Gerado pelo Sistema' },
      { value: 'external_sync', label: 'Sincronização Externa' },
      { value: 'migration', label: 'Migração de Dados' }
    ],
    placeholder: 'Selecione a fonte...',
    category: 'Auditoria'
  },
  {
    columnId: 'lastSyncAt',
    label: 'Última Sincronização',
    type: 'date',
    operators: ['equals', 'greaterThan', 'lessThan', 'between', 'isEmpty', 'isNotEmpty'],
    placeholder: 'dd/mm/aaaa',
    category: 'Auditoria'
  },
  
  // ===== CAMPOS CUSTOMIZÁVEIS =====
  {
    columnId: 'customField1',
    label: 'Campo Customizado 1',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Digite o valor...',
    category: 'Campos Customizados'
  },
  {
    columnId: 'customField2',
    label: 'Campo Customizado 2',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Digite o valor...',
    category: 'Campos Customizados'
  },
  {
    columnId: 'customField3',
    label: 'Campo Customizado 3',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Digite o número...',
    category: 'Campos Customizados'
  },
  {
    columnId: 'tags',
    label: 'Tags/Labels',
    type: 'multiselect',
    operators: ['in', 'notIn', 'contains', 'isEmpty', 'isNotEmpty'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione as tags...',
    category: 'Campos Customizados'
  },
  {
    columnId: 'internalNotes',
    label: 'Observações Internas',
    type: 'text',
    operators: ['contains', 'equals', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Digite as observações...',
    category: 'Campos Customizados'
  },
  {
    columnId: 'priority',
    label: 'Prioridade',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'greaterThan', 'lessThan'],
    options: [
      { value: 'critical', label: 'Crítica' },
      { value: 'high', label: 'Alta' },
      { value: 'medium', label: 'Média' },
      { value: 'low', label: 'Baixa' },
      { value: 'none', label: 'Não Definida' }
    ],
    placeholder: 'Selecione a prioridade...',
    category: 'Campos Customizados'
  },
  {
    columnId: 'customStatus',
    label: 'Status Customizado',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'flagged', label: 'Sinalizado' },
      { value: 'starred', label: 'Destacado' },
      { value: 'archived', label: 'Arquivado' },
      { value: 'pending_review', label: 'Pendente Revisão' },
      { value: 'approved', label: 'Aprovado' },
      { value: 'rejected', label: 'Rejeitado' },
      { value: 'on_hold', label: 'Em Espera' }
    ],
    placeholder: 'Selecione o status...',
    category: 'Campos Customizados'
  }
]

/**
 * Acessores para campos do Lote 8
 * Campos de metadados, auditoria e customizações
 */
export const LOTE_8_FIELD_ACCESSORS = {
  // Metadados diretos
  createdAt: (item: any) => item.createdAt,
  updatedAt: (item: any) => item.updatedAt,
  createdBy: (item: any) => item.createdBy,
  lastUpdatedBy: (item: any) => item.lastUpdatedBy,
  lastSyncAt: (item: any) => item.lastSyncAt,
  dataSource: (item: any) => item.dataSource || 'manual',
  syncStatus: (item: any) => item.syncStatus || 'synchronized',
  
  // Campos calculados
  updateCount: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.updateCount !== undefined) return item.updateCount
    
    // Calcular baseado no histórico de atualizações
    const updateHistory = item.updateHistory || []
    return updateHistory.length || 0
  },
  
  // Campos customizados diretos
  customField1: (item: any) => item.customField1,
  customField2: (item: any) => item.customField2,
  customField3: (item: any) => item.customField3,
  internalNotes: (item: any) => item.internalNotes,
  priority: (item: any) => item.priority || 'none',
  customStatus: (item: any) => item.customStatus,
  
  // Tags como array
  tags: (item: any) => {
    if (Array.isArray(item.tags)) return item.tags
    if (typeof item.tags === 'string') {
      // Se for string separada por vírgula
      return item.tags.split(',').map((tag: string) => tag.trim()).filter(Boolean)
    }
    return []
  }
}

/**
 * Presets estratégicos para Lote 8
 */
export const LOTE_8_PRESETS = [
  {
    id: 'registros_recentes',
    name: 'Registros Recentes',
    description: 'Colaboradores criados nos últimos 30 dias',
    filters: [
      {
        columnId: 'createdAt',
        operator: 'greaterThan' as const,
        value: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        type: 'date' as const,
        label: 'Data de Criação',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'atualizados_recentemente',
    name: 'Atualizados Recentemente',
    description: 'Colaboradores modificados na última semana',
    filters: [
      {
        columnId: 'updatedAt',
        operator: 'greaterThan' as const,
        value: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        type: 'date' as const,
        label: 'Data de Última Atualização',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'problemas_sincronizacao',
    name: 'Problemas de Sincronização',
    description: 'Registros com erro ou pendentes de sincronização',
    filters: [
      {
        columnId: 'syncStatus',
        operator: 'in' as const,
        value: ['pending', 'error', 'outdated'],
        type: 'multiselect' as const,
        label: 'Status de Sincronização',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'alta_prioridade',
    name: 'Alta Prioridade',
    description: 'Colaboradores com prioridade crítica ou alta',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'priority',
        operator: 'in' as const,
        value: ['critical', 'high'],
        type: 'multiselect' as const,
        label: 'Prioridade',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'colaboradores_sinalizados',
    name: 'Colaboradores Sinalizados',
    description: 'Registros marcados como sinalizados ou em revisão',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'customStatus',
        operator: 'in' as const,
        value: ['flagged', 'pending_review'],
        type: 'multiselect' as const,
        label: 'Status Customizado',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'muito_atualizados',
    name: 'Muito Atualizados',
    description: 'Colaboradores com muitas atualizações (>10)',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'updateCount',
        operator: 'greaterThan' as const,
        value: 10,
        type: 'number' as const,
        label: 'Número de Atualizações',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'com_observacoes',
    name: 'Com Observações',
    description: 'Colaboradores com observações internas registradas',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'internalNotes',
        operator: 'isNotEmpty' as const,
        value: null,
        type: 'text' as const,
        label: 'Observações Internas',
        enabled: true
      }
    ],
    isSystem: true
  }
] as const